/**
 * The per-entity configuration for the generic enrichment pages (MOO-4803).
 *
 * One entry per enriched entity, read off that entity's backend DTO — never
 * guessed. Everything the page and the editor need to know about an entity
 * that is not derivable from the runtime value lives here: where the
 * enrichment fields sit, which of the pipeline-owned members it has, the
 * feature-dimension labels, and the two places a back link may point.
 *
 * `config.ts` and `field-rules.ts` are a pair: this file is DATA, `field-rules`
 * is the ALGORITHM that turns a value plus one of these rules into a control.
 * The type-only import in `field-rules.ts` keeps that pair acyclic at runtime.
 */
import {
  MOVIE_FEATURE_LABELS,
  PERSON_FEATURE_LABELS,
  SHOW_GENRE_FEATURE_LABELS,
} from "@/lib/enrichment";

import {
  DOCUMENT_ONLY_KEYS,
  EnrichmentSchemaError,
  isPlainObject,
  labelForKey,
} from "./field-rules";

/** The eleven enriched entities, keyed by their Refine resource name. */
export type EnrichmentEntityKey =
  | "artists"
  | "venues"
  | "events"
  | "genres"
  | "geodata"
  | "movies"
  | "people"
  | "screenings"
  | "show-genres"
  | "plays"
  | "performances";

/** Where the enrichment fields sit on the document. Genre is the only "flat". */
export type EnrichmentShape = "enrichment" | "flat";

export type EnrichmentControl =
  | "text" // single-line Input
  | "textarea" // Textarea (about, shortDescription, overview)
  | "area" // the venue's city area, picked by name (`AreaPicker`); stored as text
  | "stringList" // chip editor over string[]  (reuses the `tagList` AutoForm field)
  | "faq" // repeatable { question, answer }
  | "features" // 0-1 slider grid, labels from `featureLabels`
  | "genreRefs" // repeatable { id, name } (reuses the `genres` AutoForm field)
  | "boolean" // Switch
  | "object" // recurse, render each child by its own rule
  | "json" // read-only JsonEnrichmentEditor fallback
  | "hidden"; // carried through, never rendered

export interface EnrichmentFieldRule {
  key: string;
  label?: string; // falls back to a de-camel-cased key
  control: EnrichmentControl;
  required?: boolean; // informational; drives the "*" marker and the empty-value fill
  clearable?: boolean; // string fields only -> "" when cleared
  featureLabels?: Record<string, string>;
}

export interface EnrichmentPageConfig {
  /** Which of the eleven this is. Redundant with the map key; handy on the page. */
  key: EnrichmentEntityKey;
  /** Refine resource name, e.g. "show-genres". Used by getOne/mutate. */
  resource: string;
  /** URL segment for the route and the API path, e.g. "show-genres". */
  path: string;
  /** Human noun for headings and toasts, e.g. "Show genre". */
  noun: string;
  /** "enrichment" for ten entities; "flat" for genre. */
  shape: EnrichmentShape;
  /** false for geodata, which has no transliterations member at all. */
  hasTransliterations: boolean;
  /** false for genre, whose enrich handler discards taskName anyway. */
  hasTaskName: boolean;
  /** Where the page's back button and the summary link point. */
  backPath: (id: string) => string;
  /** The one-line summary the trimmed surfaces show. */
  summaryOf: (enrichment: Record<string, unknown>) => string | undefined;
  /** Field rules, in display order. Unknown keys fall back to a derived rule. */
  fields: EnrichmentFieldRule[];
  /** true only for artists, venues, events, geodata (D6). */
  hasEnrichTrigger: boolean;
  /** Label for the entity's name in the page header. */
  nameOf: (record: Record<string, unknown>) => string | undefined;
}

// ---------------------------------------------------------------------------
// Translation locales
// ---------------------------------------------------------------------------

/**
 * The locale table, in the order `EnrichmentTranslationsPatch.AllowedLocales`
 * lists it (`EnrichmentTranslationsPatch.cs:48-51`). The set is **ordinal and
 * case-sensitive** — `"FR"` and `""` are rejected by the API with a 400 — and
 * it must move in lockstep with Tina's `src/enrichment/shared/locales.py`.
 */
export const LOCALES = [
  { code: "en", label: "English" },
  { code: "el", label: "Greek" },
  { code: "ar", label: "Arabic" },
  { code: "ro", label: "Romanian" },
  { code: "fr", label: "French" },
  { code: "de", label: "German" },
  { code: "es", label: "Spanish" },
  { code: "pl", label: "Polish" },
  { code: "nl", label: "Dutch" },
  { code: "it", label: "Italian" },
  { code: "pt", label: "Portuguese" },
  { code: "sv", label: "Swedish" },
  { code: "hu", label: "Hungarian" },
  { code: "fi", label: "Finnish" },
  { code: "bg", label: "Bulgarian" },
] as const;

export type TranslationLocale = (typeof LOCALES)[number]["code"];

/** The 15 locale codes, in the API's order. */
export const TRANSLATION_LOCALES: readonly TranslationLocale[] = LOCALES.map(
  (locale) => locale.code,
);

/** Locale code -> display name, for the picker and the chips. */
export const LOCALE_LABELS: Record<TranslationLocale, string> =
  Object.fromEntries(
    LOCALES.map((locale) => [locale.code, locale.label]),
  ) as Record<TranslationLocale, string>;

// ---------------------------------------------------------------------------
// Feature-dimension labels
// ---------------------------------------------------------------------------

/**
 * Feature-dimension labels, keyed by the backend Firestore property name.
 *
 * The artist (8), venue (12), event (23) and genre (8) label maps live here
 * rather than beside the three that already sit in `@/lib/enrichment` because
 * MOO-4803 phase 1 may not touch a file outside `src/components/enrichment/`.
 * Moving all four into `@/lib/enrichment.ts` is the follow-up the design's C.8
 * already schedules; nothing else changes when they move.
 */
export const ARTIST_FEATURE_LABELS: Record<string, string> = {
  energy: "Energy",
  polish: "Polish",
  queerness: "Queerness",
  nostalgia: "Nostalgia",
  locality: "Locality",
  age: "Age (Young→Old)",
  fashion: "Fashion",
  commerciality: "Commerciality",
};

export const VENUE_FEATURE_LABELS: Record<string, string> = {
  capacity: "Capacity",
  outdoor: "Outdoor",
  nature: "Nature",
  view: "View",
  seating: "Seating",
  energy: "Energy",
  rawness: "Rawness",
  undergroundness: "Underground",
  dining: "Dining",
  selectivity: "Selectivity",
  age: "Age (Young→Old)",
  queerness: "Queerness",
};

/**
 * The 23 event features, grouped exactly as `event.aienrichment.form.ts` groups
 * them: 9 venue-only, 8 lineup-only, 6 blended. The design document says "24";
 * the DTO and the existing submit schema both say 23, and they win.
 */
export const EVENT_FEATURE_LABELS: Record<string, string> = {
  // 9 venue-only
  capacity: "Capacity",
  outdoor: "Outdoor",
  nature: "Nature",
  view: "View",
  seating: "Seating",
  dining: "Dining",
  rawness: "Rawness",
  undergroundness: "Undergroundness",
  selectivity: "Selectivity",
  // 8 lineup-only
  polish: "Polish",
  nostalgia: "Nostalgia",
  locality: "Locality",
  fashion: "Fashion",
  commerciality: "Commerciality",
  tempo: "Tempo",
  vocality: "Vocality",
  organicness: "Organicness",
  // 6 blended
  energy: "Energy",
  age: "Age (Young→Old)",
  queerness: "Queerness",
  danceability: "Danceability",
  valence: "Valence",
  grit: "Grit",
};

export const GENRE_FEATURE_LABELS: Record<string, string> = {
  commerciality: "Commerciality",
  danceability: "Danceability",
  energy: "Energy",
  grit: "Grit",
  organicness: "Organicness",
  tempo: "Tempo",
  valence: "Valence",
  vocality: "Vocality",
};

// ---------------------------------------------------------------------------
// Per-entity helpers
// ---------------------------------------------------------------------------

/**
 * `/<path>/edit/<id>` — every enrichable entity has a real edit page now. People, screenings and
 * show-genres were the last three; until their editors shipped, their edit routes were a
 * placeholder and their back link went to `show/:id`.
 */
const editPath = (path: string) => (id: string) => `/${path}/edit/${id}`;

/** The entity's own name, preferring the entity table's own column names. */
function nameOfRecord(record: Record<string, unknown>): string | undefined {
  const candidate = record?.name ?? record?.title ?? record?.normalizedTitle;
  return typeof candidate === "string" && candidate.length > 0
    ? candidate
    : undefined;
}

/** The one truncated line the trimmed surfaces (D4, D5) show. */
function summaryLine(enrichment: Record<string, unknown>): string | undefined {
  for (const key of ["about", "shortDescription", "overview", "seoTitle"]) {
    const value = enrichment?.[key];
    if (typeof value === "string" && value.trim().length > 0) return value;
  }
  return undefined;
}

// ---------------------------------------------------------------------------
// The eleven configs
// ---------------------------------------------------------------------------

export const ENRICHMENT_CONFIGS: Record<
  EnrichmentEntityKey,
  EnrichmentPageConfig
> = {
  artists: {
    key: "artists",
    resource: "artists",
    path: "artists",
    noun: "Artist",
    shape: "enrichment",
    hasTransliterations: true,
    hasTaskName: true,
    backPath: editPath("artists"),
    summaryOf: summaryLine,
    hasEnrichTrigger: true,
    nameOf: nameOfRecord,
    fields: [
      {
        key: "about",
        label: "About",
        control: "textarea",
        required: true,
        clearable: true,
      },
      {
        key: "shortDescription",
        label: "Short description",
        control: "textarea",
        required: true,
        clearable: true,
      },
      { key: "seoTitle", label: "SEO title", control: "text", clearable: true },
      {
        key: "normalizedTitle",
        label: "Normalized title",
        control: "text",
        required: true,
        clearable: true,
      },
      {
        key: "keywords",
        label: "Keywords",
        control: "stringList",
        required: true,
      },
      {
        key: "relatedSearches",
        label: "Related searches",
        control: "stringList",
        required: true,
      },
      { key: "faq", label: "FAQ", control: "faq" },
      {
        key: "features",
        label: "Features",
        control: "features",
        required: true,
        featureLabels: ARTIST_FEATURE_LABELS,
      },
      { key: "genres", label: "Genres", control: "genreRefs" },
      { key: "isGroup", label: "Is group", control: "boolean" },
    ],
  },
  venues: {
    key: "venues",
    resource: "venues",
    path: "venues",
    noun: "Venue",
    shape: "enrichment",
    hasTransliterations: true,
    hasTaskName: true,
    backPath: editPath("venues"),
    summaryOf: summaryLine,
    hasEnrichTrigger: true,
    nameOf: nameOfRecord,
    fields: [
      {
        key: "about",
        label: "About",
        control: "textarea",
        required: true,
        clearable: true,
      },
      {
        key: "shortDescription",
        label: "Short description",
        control: "textarea",
        required: true,
        clearable: true,
      },
      { key: "seoTitle", label: "SEO title", control: "text", clearable: true },
      {
        key: "normalizedTitle",
        label: "Normalized title",
        control: "text",
        required: true,
        clearable: true,
      },
      {
        key: "keywords",
        label: "Keywords",
        control: "stringList",
        required: true,
      },
      {
        key: "relatedSearches",
        label: "Related searches",
        control: "stringList",
        required: true,
      },
      { key: "faq", label: "FAQ", control: "faq" },
      { key: "aliases", label: "Aliases", control: "stringList" },
      { key: "area", label: "Area", control: "area", clearable: true },
      {
        key: "type",
        label: "Type",
        control: "text",
        required: true,
        clearable: true,
      },
      {
        key: "hiddenType",
        label: "Hidden type",
        control: "text",
        required: true,
        clearable: true,
      },
      { key: "tags", label: "Tags", control: "stringList", required: true },
      { key: "photos", label: "Photos", control: "json", required: true },
      {
        key: "features",
        label: "Features",
        control: "features",
        required: true,
        featureLabels: VENUE_FEATURE_LABELS,
      },
    ],
  },
  events: {
    key: "events",
    resource: "events",
    path: "events",
    noun: "Event",
    shape: "enrichment",
    hasTransliterations: true,
    hasTaskName: true,
    backPath: editPath("events"),
    summaryOf: summaryLine,
    hasEnrichTrigger: true,
    nameOf: nameOfRecord,
    fields: [
      {
        key: "shortDescription",
        label: "Short description",
        control: "textarea",
        required: true,
        clearable: true,
      },
      { key: "seoTitle", label: "SEO title", control: "text", clearable: true },
      {
        key: "normalizedTitle",
        label: "Normalized title",
        control: "text",
        required: true,
        clearable: true,
      },
      {
        key: "keywords",
        label: "Keywords",
        control: "stringList",
        required: true,
      },
      {
        key: "relatedSearches",
        label: "Related searches",
        control: "stringList",
        required: true,
      },
      { key: "faq", label: "FAQ", control: "faq" },
      {
        key: "aliases",
        label: "Aliases",
        control: "stringList",
        required: true,
      },
      {
        key: "features",
        label: "Features",
        control: "features",
        featureLabels: EVENT_FEATURE_LABELS,
      },
    ],
  },
  genres: {
    key: "genres",
    resource: "genres",
    path: "genres",
    noun: "Genre",
    // Genre is the structural outlier: `about`, `aliases`, `parents`,
    // `features`, `transliterations` and `translations` sit on the document
    // ROOT, not under an `enrichment` map, and `id`/`name` are the entity's.
    shape: "flat",
    hasTransliterations: true,
    hasTaskName: false,
    backPath: editPath("genres"),
    summaryOf: summaryLine,
    hasEnrichTrigger: false,
    nameOf: nameOfRecord,
    fields: [
      {
        key: "about",
        label: "About",
        control: "textarea",
        required: true,
        clearable: true,
      },
      { key: "aliases", label: "Aliases", control: "stringList" },
      { key: "parents", label: "Parents", control: "stringList" },
      {
        key: "features",
        label: "Features",
        control: "features",
        required: true,
        featureLabels: GENRE_FEATURE_LABELS,
      },
      // Not part of `GenreEnrichmentPayload`, so it can never be saved through
      // `/genres/enrich`. Read-only is the honest rendering — an editable
      // `metadata` here would be a silent no-op.
      { key: "metadata", label: "Metadata", control: "json" },
    ],
  },
  geodata: {
    key: "geodata",
    resource: "geodata",
    path: "geodata",
    noun: "Geodata",
    shape: "enrichment",
    // Geodata has no `transliterations` member at all, and its enrichment
    // persists through a five-key whitelist converter.
    hasTransliterations: false,
    hasTaskName: true,
    backPath: editPath("geodata"),
    summaryOf: summaryLine,
    hasEnrichTrigger: true,
    nameOf: nameOfRecord,
    fields: [
      // `about` and `aliases` must be PRESENT as ""/[]: the geodata handler
      // writes them with `SetNested`, which does not skip nulls.
      {
        key: "about",
        label: "About",
        control: "textarea",
        required: true,
        clearable: true,
      },
      {
        key: "aliases",
        label: "Aliases",
        control: "stringList",
        required: true,
      },
    ],
  },
  movies: {
    key: "movies",
    resource: "movies",
    path: "movies",
    noun: "Movie",
    shape: "enrichment",
    hasTransliterations: true,
    hasTaskName: true,
    backPath: editPath("movies"),
    summaryOf: summaryLine,
    hasEnrichTrigger: false,
    nameOf: nameOfRecord,
    fields: [
      {
        key: "shortDescription",
        label: "Short description",
        control: "textarea",
        required: true,
        clearable: true,
      },
      {
        key: "overview",
        label: "Overview",
        control: "textarea",
        clearable: true,
      },
      { key: "seoTitle", label: "SEO title", control: "text", clearable: true },
      {
        key: "normalizedTitle",
        label: "Normalized title",
        control: "text",
        required: true,
        clearable: true,
      },
      {
        key: "keywords",
        label: "Keywords",
        control: "stringList",
        required: true,
      },
      {
        key: "relatedSearches",
        label: "Related searches",
        control: "stringList",
        required: true,
      },
      { key: "faq", label: "FAQ", control: "faq" },
      {
        key: "features",
        label: "Features",
        control: "features",
        required: true,
        featureLabels: MOVIE_FEATURE_LABELS,
      },
    ],
  },
  people: {
    key: "people",
    resource: "people",
    path: "people",
    noun: "Person",
    shape: "enrichment",
    hasTransliterations: true,
    hasTaskName: true,
    backPath: editPath("people"),
    summaryOf: summaryLine,
    hasEnrichTrigger: false,
    nameOf: nameOfRecord,
    fields: [
      { key: "about", label: "About", control: "textarea", clearable: true },
      {
        key: "shortDescription",
        label: "Short description",
        control: "textarea",
        required: true,
        clearable: true,
      },
      { key: "seoTitle", label: "SEO title", control: "text", clearable: true },
      {
        key: "normalizedTitle",
        label: "Normalized title",
        control: "text",
        required: true,
        clearable: true,
      },
      {
        key: "keywords",
        label: "Keywords",
        control: "stringList",
        required: true,
      },
      {
        key: "relatedSearches",
        label: "Related searches",
        control: "stringList",
        required: true,
      },
      { key: "faq", label: "FAQ", control: "faq" },
      {
        key: "features",
        label: "Features",
        control: "features",
        required: true,
        featureLabels: PERSON_FEATURE_LABELS,
      },
    ],
  },
  screenings: {
    key: "screenings",
    resource: "screenings",
    path: "screenings",
    noun: "Screening",
    shape: "enrichment",
    hasTransliterations: true,
    hasTaskName: true,
    backPath: editPath("screenings"),
    summaryOf: summaryLine,
    hasEnrichTrigger: false,
    nameOf: nameOfRecord,
    fields: [
      {
        key: "shortDescription",
        label: "Short description",
        control: "textarea",
        required: true,
        clearable: true,
      },
      { key: "seoTitle", label: "SEO title", control: "text", clearable: true },
      {
        key: "normalizedTitle",
        label: "Normalized title",
        control: "text",
        required: true,
        clearable: true,
      },
      {
        key: "keywords",
        label: "Keywords",
        control: "stringList",
        required: true,
      },
      {
        key: "relatedSearches",
        label: "Related searches",
        control: "stringList",
        required: true,
      },
      { key: "faq", label: "FAQ", control: "faq" },
    ],
  },
  "show-genres": {
    key: "show-genres",
    resource: "show-genres",
    path: "show-genres",
    noun: "Show genre",
    shape: "enrichment",
    hasTransliterations: true,
    hasTaskName: true,
    backPath: editPath("show-genres"),
    summaryOf: summaryLine,
    hasEnrichTrigger: false,
    nameOf: nameOfRecord,
    fields: [
      { key: "about", label: "About", control: "textarea", clearable: true },
      { key: "aliases", label: "Aliases", control: "stringList" },
      { key: "parents", label: "Parents", control: "stringList" },
      {
        key: "features",
        label: "Features",
        control: "features",
        required: true,
        featureLabels: SHOW_GENRE_FEATURE_LABELS,
      },
    ],
  },
  // MOO-4814 task 17. `PlayAIEnrichmentDbDto` is movie's shape minus
  // `reasoning`/`features`/`rating`/`topReviews` — no feature-score control,
  // and (unlike movie's read-only `reasoning`) play carries no `reasoning`
  // member on its Firestore DTO either. `fields[]` is exactly movie's, minus
  // `features`.
  plays: {
    key: "plays",
    resource: "plays",
    path: "plays",
    noun: "Play",
    shape: "enrichment",
    hasTransliterations: true,
    hasTaskName: true,
    backPath: editPath("plays"),
    summaryOf: summaryLine,
    hasEnrichTrigger: false,
    nameOf: nameOfRecord,
    fields: [
      {
        key: "shortDescription",
        label: "Short description",
        control: "textarea",
        required: true,
        clearable: true,
      },
      {
        key: "overview",
        label: "Overview",
        control: "textarea",
        clearable: true,
      },
      { key: "seoTitle", label: "SEO title", control: "text", clearable: true },
      {
        key: "normalizedTitle",
        label: "Normalized title",
        control: "text",
        required: true,
        clearable: true,
      },
      {
        key: "keywords",
        label: "Keywords",
        control: "stringList",
        required: true,
      },
      {
        key: "relatedSearches",
        label: "Related searches",
        control: "stringList",
        required: true,
      },
      { key: "faq", label: "FAQ", control: "faq" },
    ],
  },
  // `PerformanceAIEnrichmentDbDto` — the shared SEO/GEO contract only, exactly
  // like screening (no `reasoning`/`features`/`overview`). `fields[]` is
  // exactly screenings'.
  performances: {
    key: "performances",
    resource: "performances",
    path: "performances",
    noun: "Performance",
    shape: "enrichment",
    hasTransliterations: true,
    hasTaskName: true,
    backPath: editPath("performances"),
    summaryOf: summaryLine,
    hasEnrichTrigger: false,
    nameOf: nameOfRecord,
    fields: [
      {
        key: "shortDescription",
        label: "Short description",
        control: "textarea",
        required: true,
        clearable: true,
      },
      { key: "seoTitle", label: "SEO title", control: "text", clearable: true },
      {
        key: "normalizedTitle",
        label: "Normalized title",
        control: "text",
        required: true,
        clearable: true,
      },
      {
        key: "keywords",
        label: "Keywords",
        control: "stringList",
        required: true,
      },
      {
        key: "relatedSearches",
        label: "Related searches",
        control: "stringList",
        required: true,
      },
      { key: "faq", label: "FAQ", control: "faq" },
    ],
  },
};

/** The eleven, in menu order. */
export const ENRICHMENT_RESOURCES: readonly EnrichmentEntityKey[] = [
  "artists",
  "venues",
  "events",
  "genres",
  "geodata",
  "movies",
  "people",
  "screenings",
  "show-genres",
  "plays",
  "performances",
];

/**
 * The config for a resource name.
 *
 * Throws rather than returning a default: a typo'd resource would otherwise
 * render an empty page that PUTs an empty body at the wrong endpoint, and the
 * enrich PUT is a full replace.
 */
export function enrichmentConfigFor(resource: string): EnrichmentPageConfig {
  const config = ENRICHMENT_CONFIGS[resource as EnrichmentEntityKey];
  if (!config) {
    throw new EnrichmentSchemaError(
      resource,
      `no enrichment config for resource "${resource}"`,
    );
  }
  return config;
}

/**
 * Whether a status means "this entity still wants enrichment".
 *
 * Moved here verbatim from the three review pages, which each redeclared it.
 */
export function needsEnrichment(status: string | null | undefined): boolean {
  return (
    !status ||
    status === "Unavailable" ||
    status === "NeedsReview" ||
    status === "Failed" ||
    status === "Enriched"
  );
}

/**
 * The object the editor edits, for either shape.
 *
 * Nested entities hand back `record.enrichment`; genre hands back the document
 * itself, minus the keys that belong to the entity rather than to its
 * enrichment (`id`, `name`, the lifecycle statuses, the audit fields) — those
 * are read-only elsewhere and must not grow an editor here.
 */
export function enrichmentOf(
  config: EnrichmentPageConfig,
  record: unknown,
): Record<string, unknown> {
  if (!isPlainObject(record)) return {};
  if (config.shape === "flat") {
    const slice: Record<string, unknown> = {};
    for (const [key, value] of Object.entries(record)) {
      if (!DOCUMENT_ONLY_KEYS.has(key)) slice[key] = value;
    }
    return slice;
  }
  return isPlainObject(record.enrichment) ? record.enrichment : {};
}

/** A field rule's label, falling back to the de-camel-cased key. */
export function labelOf(rule: { key: string; label?: string }): string {
  return rule.label ?? labelForKey(rule.key);
}
